import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Shield, Database, Lock, Eye, UserCheck, Globe, Bell, Trash2, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: Database,
      title: "1. Données collectées",
      content: `DOCKHTA collecte les données suivantes pour fournir ses services :

Données d'identification :
• Nom complet, date de naissance, genre
• Adresse email et numéro de téléphone
• Photo de profil (optionnel)
• Localisation géographique

Données de santé (pour les patients) :
• Historique des consultations et rendez-vous
• Documents médicaux téléchargés (ordonnances, examens)
• Conversations avec les professionnels de santé
• Interactions avec l'assistant IA santé

Données professionnelles (pour les praticiens) :
• Diplômes et certifications
• Numéro d'ordre professionnel
• Spécialités et tarifs
• Disponibilités et horaires`
    },
    {
      icon: Lock,
      title: "2. Finalités du traitement",
      content: `Vos données sont utilisées pour :

• Création et gestion de votre compte utilisateur
• Mise en relation avec les professionnels de santé
• Prise de rendez-vous et téléconsultations
• Stockage sécurisé de vos documents médicaux
• Amélioration de nos services et de l'expérience utilisateur
• Envoi de notifications et rappels de rendez-vous
• Support client et assistance technique
• Respect de nos obligations légales

Nous ne vendons jamais vos données personnelles à des tiers.`
    },
    {
      icon: Shield,
      title: "3. Protection des données de santé",
      content: `Les données de santé bénéficient d'une protection renforcée :

• Chiffrement de bout en bout des données médicales
• Stockage sur des serveurs sécurisés certifiés
• Accès restreint aux seuls personnels autorisés
• Journalisation de tous les accès aux données sensibles
• Sauvegardes régulières et redondantes

Conformité aux réglementations :
• Loi camerounaise sur la protection des données personnelles
• Standards internationaux de sécurité des données de santé
• Principes du RGPD européen pour les bonnes pratiques`
    },
    {
      icon: Eye,
      title: "4. Partage des données",
      content: `Vos données peuvent être partagées avec :

Professionnels de santé :
• Uniquement ceux avec qui vous avez rendez-vous
• Dans le cadre strict de votre prise en charge médicale

Prestataires techniques :
• Hébergeurs cloud sécurisés (Cloudinary pour les médias)
• Services de paiement (Mobile Money, cartes bancaires)
• Services d'email transactionnel

Nous ne partageons jamais vos données avec :
• Des annonceurs publicitaires
• Des courtiers en données
• Des entreprises non essentielles à nos services`
    },
    {
      icon: UserCheck,
      title: "5. Vos droits",
      content: `Conformément à la réglementation, vous disposez des droits suivants :

Droit d'accès :
Obtenez une copie de toutes vos données personnelles

Droit de rectification :
Corrigez les informations inexactes ou incomplètes

Droit à l'effacement :
Demandez la suppression de vos données (sous conditions)

Droit à la portabilité :
Récupérez vos données dans un format structuré

Droit d'opposition :
Refusez certains traitements de vos données

Pour exercer ces droits, contactez : privacy@dockhta.com`
    },
    {
      icon: Bell,
      title: "6. Cookies et traceurs",
      content: `DOCKHTA utilise des cookies pour :

Cookies essentiels (obligatoires) :
• Authentification et sécurité de session
• Mémorisation des préférences de langue
• Fonctionnement technique de la plateforme

Cookies analytiques (optionnels) :
• Mesure d'audience anonymisée
• Amélioration des performances
• Détection des problèmes techniques

Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.`
    },
    {
      icon: Globe,
      title: "7. Transferts internationaux",
      content: `Vos données sont principalement stockées au Cameroun et en Afrique.

En cas de transfert vers d'autres pays :
• Nous nous assurons d'un niveau de protection adéquat
• Des clauses contractuelles types sont mises en place
• Vous êtes informé de la destination des données

Nos partenaires cloud respectent les standards de sécurité internationaux.`
    },
    {
      icon: Trash2,
      title: "8. Conservation des données",
      content: `Durée de conservation :

Données de compte :
• Conservées pendant toute la durée de votre inscription
• Supprimées 3 ans après la dernière connexion (compte inactif)

Données médicales :
• Conservées conformément aux obligations légales (20 ans minimum)
• Archivées de manière sécurisée

Logs techniques :
• Conservés pendant 12 mois maximum

Vous pouvez demander la suppression de votre compte à tout moment.`
    },
    {
      icon: Mail,
      title: "9. Contact DPO",
      content: `Pour toute question concernant vos données personnelles :

Délégué à la Protection des Données (DPO)
Email : privacy@dockhta.com
Adresse : Yaoundé, Cameroun
Téléphone : +237 621 05 53 65

Nous nous engageons à répondre à toute demande dans un délai de 30 jours.

En cas de désaccord, vous pouvez saisir l'autorité de protection des données compétente.`
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l&apos;accueil
            </Button>
          </Link>
          <Link to="/" className="text-2xl font-bold text-[#E53935]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DOCKHTA
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#1976D2]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#1976D2]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Politique de Confidentialité
          </h1>
          <p className="text-slate-600">
            Dernière mise à jour : Juin 2026
          </p>
        </div>

        {/* Introduction */}
        <Card className="border-0 shadow-sm mb-8 bg-gradient-to-r from-[#1976D2]/5 to-[#1976D2]/10">
          <CardContent className="p-6 md:p-8">
            <p className="text-slate-700 leading-relaxed">
              Chez DOCKHTA, la protection de vos données personnelles et de santé est notre priorité absolue. 
              Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
              Nous nous engageons à respecter votre vie privée et à assurer la sécurité de vos données médicales sensibles.
            </p>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#1976D2]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#1976D2]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 mb-3">
                        {section.title}
                      </h2>
                      <div className="text-slate-600 whitespace-pre-line leading-relaxed">
                        {section.content}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer info */}
        <div className="mt-12 text-center text-slate-500 text-sm">
          <p>
            En utilisant DOCKHTA, vous acceptez cette Politique de Confidentialité.
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="text-[#1976D2] hover:underline">
              Conditions d&apos;Utilisation
            </Link>
            <span>•</span>
            <Link to="/mentions-legales" className="text-[#1976D2] hover:underline">
              Mentions Légales
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
